import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import swal from "sweetalert2";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import { DetalleServicioComponent } from "../detalle/detalle-servicio.component";

interface ServicioCard {
  id: string;
  titulo: string;
  aplicacion: string;
  client: string;
  categoria: string;
  horasContratadas: number;
  horasUsadas: number;
  horasUsadasCalculadas: number;
  saldoHoras: number;
  porcentajeConsumo: number;
  fechaInicio: string;
  fechaFin: string;
  owner: string;
  role: string;
  avatar: string;
  note: string;
  noteDate: string;
}

interface ResumenServiciosResponse {
  totalServicios: number;
  horasContratadasTotales: number;
  horasUsadasTotales: number;
  porcentajeConsumoTotal: number;
  saldoHorasTotal: number;
  services: ServicioCard[];
}

interface FilterOption {
  id: number | null;
  text: string;
}

interface FiltrosServiciosResponse {
  tiposServicio: FilterOption[];
  clients: FilterOption[];
  responsables: FilterOption[];
  anios: FilterOption[];
}

type PaginationItem = number | "...";

@Component({
  selector: "app-consulta-servicios",
  standalone: true,
  templateUrl: "./consulta-servicios.component.html",
  styleUrls: ["./consulta-servicios.component.css"],
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatIconModule],
})
export class ConsultaServiciosComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly filtrosCambio$ = new Subject<void>();

  selectedTipoServicioId: number | null = null;
  selectedClientId: number | null = null;
  selectedResponsableId: number | null = null;
  selectedAnioVigencia: number | null = new Date().getFullYear();
  isLoading = false;

  readonly defaultTipoServicioOption: FilterOption = { id: null, text: "Todos" };
  readonly defaultClientOption: FilterOption = { id: null, text: "Todos" };
  readonly defaultResponsableOption: FilterOption = { id: null, text: "Todos" };
  readonly defaultAnioOption: FilterOption = { id: null, text: "Todos" };

  tipoServicioOptions: FilterOption[] = [this.defaultTipoServicioOption];
  clientOptions: FilterOption[] = [this.defaultClientOption];
  responsableOptions: FilterOption[] = [this.defaultResponsableOption];
  anioOptions: FilterOption[] = [this.defaultAnioOption];

  resumen: ResumenServiciosResponse | null = null;
  serviceCards: ServicioCard[] = [];
  paginatedServiceCards: ServicioCard[] = [];
  paginationItems: PaginationItem[] = [];
  currentPage = 1;
  readonly cardsPerPage = 3;

  constructor(
    private proyectoService: ProyectosService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.consultarFiltros();

    this.filtrosCambio$
      .pipe(
        debounceTime(300),
        switchMap(() => this.proyectoService.consultarResumenSeguimientoServicios(this.buildParams())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response: ResumenServiciosResponse) => {
          this.resumen = response ?? null;
          this.serviceCards = response?.services ?? [];
          this.currentPage = 1;
          this.actualizarPaginacion();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error("Error al consultar el resumen de seguimiento de servicios:", error);
          this.resumen = null;
          this.serviceCards = [];
          this.paginatedServiceCards = [];
          this.paginationItems = [];
          this.currentPage = 1;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });

    this.emitirCambioFiltros();
  }

  onFilterChange(): void {
    this.emitirCambioFiltros();
  }

  private buildParams() {
    return {
      idTipoServicio: this.selectedTipoServicioId,
      idCliente: this.selectedClientId,
      idResponsable: this.selectedResponsableId,
      anioVigencia: this.selectedAnioVigencia,
    };
  }

  private emitirCambioFiltros(): void {
    this.isLoading = true;
    this.filtrosCambio$.next();
  }

  // Un <label> que envuelve un <select> solo lo enfoca al hacer click en Chrome/Edge
  // (no abre la lista); un click directo sobre el <select> sí la abre de forma nativa.
  // Para que TODO el chip (ícono, texto, flecha) abra la lista y no solo el <select>,
  // se fuerza la apertura con showPicker() cuando el click no fue directamente sobre él.
  abrirFiltro(select: HTMLSelectElement, event: MouseEvent): void {
    if (event.target === select || select.disabled) {
      return;
    }

    select.focus();
    if (typeof (select as any).showPicker === "function") {
      try {
        (select as any).showPicker();
      } catch {
        // showPicker puede lanzar si el navegador no permite abrirlo en este contexto;
        // el foco ya aplicado permite abrirlo con teclado como respaldo.
      }
    }
  }

  private consultarFiltros(): void {
    this.proyectoService
      .consultarFiltrosSeguimientoServicios()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: FiltrosServiciosResponse) => {
          this.tipoServicioOptions = this.construirOpciones(
            response?.tiposServicio ?? [],
            this.defaultTipoServicioOption
          );
          this.clientOptions = this.construirOpciones(response?.clients ?? [], this.defaultClientOption);
          this.responsableOptions = this.construirOpciones(
            response?.responsables ?? [],
            this.defaultResponsableOption
          );
          this.anioOptions = this.construirOpcionesAnios(response?.anios ?? []);
        },
        error: (error) => {
          console.error("Error al consultar los filtros de seguimiento de servicios:", error);
          this.tipoServicioOptions = [this.defaultTipoServicioOption];
          this.clientOptions = [this.defaultClientOption];
          this.responsableOptions = [this.defaultResponsableOption];
          this.anioOptions = [this.defaultAnioOption];
        },
      });
  }

  private construirOpciones(opciones: FilterOption[], opcionDefault: FilterOption): FilterOption[] {
    const opcionesValidas = (opciones ?? [])
      .filter((option) => option?.id != null && !!option?.text?.trim())
      .map((option) => ({ id: option.id, text: option.text.trim() }))
      .sort((a, b) => a.text.localeCompare(b.text));

    return [opcionDefault, ...opcionesValidas];
  }

  private construirOpcionesAnios(opciones: FilterOption[]): FilterOption[] {
    const opcionesValidas = (opciones ?? [])
      .filter((option) => option?.id != null)
      .map((option) => ({ id: option.id, text: option.text?.trim() || String(option.id) }));

    return [this.defaultAnioOption, ...opcionesValidas];
  }

  trackByOptionId(_: number, option: FilterOption): number | null {
    return option.id;
  }

  trackByCardId(_: number, card: ServicioCard): string {
    return card.id;
  }

  get totalPages(): number {
    const total = Math.ceil(this.serviceCards.length / this.cardsPerPage);
    return total > 0 ? total : 1;
  }

  private actualizarPaginacion(): void {
    const total = this.totalPages;
    const current = this.currentPage;

    const startIndex = (current - 1) * this.cardsPerPage;
    this.paginatedServiceCards = this.serviceCards.slice(startIndex, startIndex + this.cardsPerPage);

    if (total <= 7) {
      this.paginationItems = Array.from({ length: total }, (_, i) => i + 1);
      return;
    }
    if (current <= 3) {
      this.paginationItems = [1, 2, 3, "...", total];
      return;
    }
    if (current >= total - 2) {
      this.paginationItems = [1, "...", total - 2, total - 1, total];
      return;
    }
    this.paginationItems = [1, "...", current - 1, current, current + 1, "...", total];
  }

  trackByPaginationItem(index: number): number {
    return index;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
    this.actualizarPaginacion();
  }

  onPaginationItemClick(item: PaginationItem): void {
    if (item === "...") {
      return;
    }
    this.goToPage(item);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  // Un servicio sin hrs contratadas es un escenario válido; en ese caso el concepto
  // de "saldo" no aplica y se muestra "Sin límite" en vez de un número engañoso.
  saldoLabel(servicio: ServicioCard): string {
    return servicio.horasContratadas > 0 ? `${servicio.saldoHoras} HRS` : "SIN LÍMITE";
  }

  exportarPdf(): void {
    if (this.serviceCards.length === 0) {
      swal.fire("Atención", "No hay servicios para exportar con los filtros actuales", "warning");
      return;
    }

    const encabezados = [
      "Servicio",
      "Aplicación",
      "Tipo de servicio",
      "Cliente",
      "Hrs contratadas",
      "Hrs usadas",
      "Hrs COS",
      "Saldo",
      "% Consumo",
      "Fecha inicio",
      "Fecha fin",
      "Responsable",
      "Nota",
      "Fecha nota",
    ];

    const filas = this.serviceCards.map((servicio) => [
      servicio.titulo,
      servicio.aplicacion,
      servicio.categoria,
      servicio.client,
      servicio.horasContratadas,
      servicio.horasUsadas,
      servicio.horasUsadasCalculadas,
      this.saldoLabel(servicio),
      servicio.porcentajeConsumo,
      this.formatFecha(servicio.fechaInicio),
      this.formatFecha(servicio.fechaFin),
      servicio.owner,
      servicio.note,
      servicio.noteDate,
    ]);

    const doc = new jsPDF("l", "mm", "a4");
    const fechaGeneracion = new Date().toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(183, 28, 28);
    doc.text("Reporte de Seguimiento de Servicios", 14, 15);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110, 116, 128);
    doc.text(`Generado el ${fechaGeneracion}`, 14, 21);

    autoTable(doc, {
      head: [encabezados],
      body: filas,
      startY: 26,
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [183, 28, 28], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [247, 249, 251] },
    });

    const fechaHoy = new Date().toISOString().slice(0, 10);
    doc.save(`Seguimiento_Servicios_${fechaHoy}.pdf`);
  }

  formatFecha(fecha: string | Date | null): string {
    if (!fecha) {
      return "";
    }
    const valor = new Date(fecha);
    if (Number.isNaN(valor.getTime())) {
      return "";
    }
    return valor.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  }

  openHistorial(servicio: ServicioCard): void {
    const dialogRef = this.dialog.open(DetalleServicioComponent, {
      autoFocus: false,
      width: "720px",
      maxWidth: "96vw",
      // El top navbar de la app usa position:fixed con z-index por encima del overlay
      // de MatDialog, así que un "top" en vh (5vh ~ 35-40px en la mayoría de pantallas)
      // deja el header del modal detrás del navbar (~70px de alto). Se usa un offset fijo
      // en px que garantiza despejarlo, con maxHeight ajustado para conservar el margen inferior.
      maxHeight: "calc(100vh - 110px)",
      position: { top: "90px" },
      panelClass: "custom-modalbox",
      data: {
        idProyecto: servicio.id,
        titulo: servicio.titulo,
        categoria: servicio.categoria,
        cliente: servicio.client,
      },
    });

    dialogRef.afterClosed().subscribe((huboCambios: boolean) => {
      if (huboCambios) {
        this.emitirCambioFiltros();
      }
    });
  }
}
